import { isLiquidArticleType } from "@/model/liquid-types"

/** Maximum total amount of a prepared flower article, in gram. */
export const FLOWERS_MAX_AMOUNT_G = 250

/** Maximum total volume of a liquid article, in milliliter. Applies to essences and xtracts alike. */
export const LIQUID_MAX_AMOUNT_ML = 500

/** Maximum total amount orderable for a prepared article of the given type, in its display unit. */
export function preparedTypeMaxAmount(type: string | null | undefined): number {
    return isLiquidArticleType(type) ? LIQUID_MAX_AMOUNT_ML : FLOWERS_MAX_AMOUNT_G
}

const MIN_SELECTABLE_AMOUNT = 1

type PreparedTypePackageAmountSource = {
    availableBatchPreparationPackageAmounts?: number[] | null
}

function clampMaxAmount(maxAmount: number): number {
    return Math.max(MIN_SELECTABLE_AMOUNT, Math.floor(maxAmount))
}

function createRange(from: number, to: number): number[] {
    return Array.from({ length: to - from + 1 }, (_, index) => from + index)
}

function sanatizePackageAmounts(source: PreparedTypePackageAmountSource): number[] {
    const rawPackageAmounts = source.availableBatchPreparationPackageAmounts ?? []

    return [...new Set(rawPackageAmounts)]
        .filter(packageAmount => Number.isInteger(packageAmount) && packageAmount > 0)
        .sort((a, b) => a - b)
}

function buildReachableTotals(maxAmount: number, packageAmounts: number[]): boolean[] {
    // reachable[total] indicates whether "total" can be composed from available package sizes.
    // This is the classic unbounded coin-change reachability DP.
    const reachable = Array<boolean>(maxAmount + 1).fill(false)
    reachable[0] = true

    for (const packageAmount of packageAmounts) {
        for (let total = packageAmount; total <= maxAmount; total++) {
            if (reachable[total - packageAmount]) {
                reachable[total] = true
            }
        }
    }

    return reachable
}

/**
 * Returns all selectable prepared-type amounts that can be fulfilled with the available package sizes.
 */
export function getPreparedTypeSelectableAmounts(
    source: PreparedTypePackageAmountSource | undefined,
    maxAmount = FLOWERS_MAX_AMOUNT_G,
): number[] {
    const normalizedMaxAmount = clampMaxAmount(maxAmount)

    if (!source) {
        return createRange(MIN_SELECTABLE_AMOUNT, normalizedMaxAmount)
    }

    const packageAmounts = sanatizePackageAmounts(source)
    if (packageAmounts.length === 0) {
        return createRange(MIN_SELECTABLE_AMOUNT, normalizedMaxAmount)
    }

    const reachableTotals = buildReachableTotals(normalizedMaxAmount, packageAmounts)

    return createRange(MIN_SELECTABLE_AMOUNT, normalizedMaxAmount).filter(totalAmount => reachableTotals[totalAmount])
}
